import React, { useEffect, useState } from 'react';
import * as Icons from 'react-icons/fa';
import { useConsumptionLog, ConsumptionEntry } from '../hooks/useConsumptionLog';
import { useTimer } from '../hooks/useTimer';
import { getDrugById, categories, getDrugColor } from '../data/drugs';
import { useDrugs } from '../contexts/DrugsContext';
import { Drug } from '../types';
import EffectPlot, { Dose } from './EffectPlot';

interface EntryCardProps {
  entry: ConsumptionEntry;
  onDelete: (id: string) => void;
}

const ConsumptionOverview: React.FC<{ entries: ConsumptionEntry[] }> = ({ entries }) => {
  const { drugs } = useDrugs();
  
  // Group entries by drugId and calculate total consumption
  const totals = entries.reduce((acc, entry) => {
    const drug = getDrugById(entry.drugId, drugs);
    if (!drug) return acc;
    
    if (!acc[entry.drugId]) {
      acc[entry.drugId] = {
        name: drug.name,
        category: drug.category,
        total: 0,
        unit: entry.unit,
        drugId: entry.drugId
      };
    }
    
    // Only add if the units match
    if (acc[entry.drugId].unit === entry.unit) {
      acc[entry.drugId].total += entry.dosage;
    }
    
    return acc;
  }, {} as Record<string, { name: string; category: string; total: number; unit: string; drugId: string }>);
  
  if (Object.keys(totals).length === 0) return null;
  
  return (
    <div className="mb-6 p-0">
      <div className="flex flex-wrap gap-3 justify-center">
        {Object.entries(totals).map(([key, { name, category, total, unit, drugId }]) => {
          // Use drug-specific color
          const drugColor = getDrugColor(drugId);
          return (
            <div 
              key={key} 
              className={`inline-flex items-center px-4 py-2 rounded-lg text-sm sm:text-base ${drugColor} text-white shadow-md`}
            >
              <span className="font-bold mr-2">{name}:</span>
              <span className="font-mono font-semibold">{total} {unit}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const EntryCard: React.FC<EntryCardProps> = ({ entry, onDelete }) => {
  const { getTimeSince } = useConsumptionLog();
  const { drugs } = useDrugs();
  const timer = useTimer(entry.timestamp);
  const drug = getDrugById(entry.drugId, drugs);
  
  if (!drug) return null;
  
  // Get drug-specific color instead of just category color
  const drugColor = getDrugColor(entry.drugId);
  
  const timeUntilPeak = calculateTimeUntilPeak(entry, drug);
  const remainingDuration = calculateRemainingDuration(entry, drug);
  
  const formatTime = ({ hours, minutes, seconds }: { hours: number, minutes: number, seconds: number }) => {
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  };

  const formatTimestamp = (timestamp: number) => {
    const date = new Date(timestamp);
    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');
    return `${hours}:${minutes}`;
  };
  
  return (
    <div className={`${drugColor} bg-opacity-20 border-l-4 ${drugColor} rounded-lg p-3 sm:p-4 mb-2 sm:mb-3 shadow-md`}>
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-2">
        <div className="flex-1 mb-2 sm:mb-0">
          <h3 className="text-lg sm:text-xl font-semibold text-white">{drug.name}</h3>
          <p className="text-gray-400 text-sm sm:text-base">
            <span className="font-bold">{entry.dosage} {entry.unit}</span>
          </p>
        </div>
        <button
          onClick={() => onDelete(entry.id)}
          className="text-gray-400 hover:text-red-500 transition-colors p-1 -mt-1"
          aria-label="Delete entry"
        >
          {React.createElement(Icons.FaTrash)}
        </button>
      </div>
      
      <div className="text-sm sm:text-base text-gray-400">
        <p className="mb-1">
          <span className="font-bold">Time consumed:</span>{' '}
          {formatTimestamp(entry.timestamp)} ({formatTime(timer)} ago)
        </p>
        {entry.notes && (
          <p className="mt-2 text-gray-300 italic">
            {entry.notes}
          </p>
        )}
      </div>
    </div>
  );
};

// Utility functions to calculate times
function calculateTimeUntilPeak(entry: ConsumptionEntry, drug: Drug): number | null {
  if (!drug || !drug.peakTime) return null;
  
  const elapsedMinutes = (Date.now() - entry.timestamp) / 60000;
  const remainingMinutes = drug.peakTime - elapsedMinutes;
  
  return remainingMinutes > 0 ? Math.floor(remainingMinutes) : 0;
}

function calculateRemainingDuration(entry: ConsumptionEntry, drug: Drug): number | null {
  if (!drug || !drug.duration) return null;
  
  const elapsedMinutes = (Date.now() - entry.timestamp) / 60000;
  const remainingMinutes = drug.duration - elapsedMinutes;
  
  return remainingMinutes > 0 ? Math.floor(remainingMinutes) : 0;
}

// New component for grouped drug entries
interface GroupedEntryCardProps {
  drugId: string;
  entries: ConsumptionEntry[];
  onDelete: (id: string) => void;
}

const GroupedEntryCard: React.FC<GroupedEntryCardProps> = ({ drugId, entries, onDelete }) => {
  const { drugs } = useDrugs();
  const drug = getDrugById(drugId, drugs);
  const [now, setNow] = useState(Date.now());
  
  // Update the current time every second
  useEffect(() => {
    const interval = setInterval(() => {
      setNow(Date.now());
    }, 1000);
    
    return () => clearInterval(interval);
  }, []);
  
  if (!drug || entries.length === 0) return null;
  
  // Get drug-specific color
  const drugColor = getDrugColor(drugId);

  const formatTime = (timestamp: number) => {
    const diffInSeconds = Math.floor((now - timestamp) / 1000);
    const hours = Math.floor(diffInSeconds / 3600);
    const minutes = Math.floor((diffInSeconds % 3600) / 60);
    const seconds = diffInSeconds % 60;
    
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  };

  const formatTimestamp = (timestamp: number) => {
    const date = new Date(timestamp);
    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');
    return `${hours}:${minutes}`;
  };
  
  // Sort entries by timestamp (newest first)
  const sortedEntries = [...entries].sort((a, b) => b.timestamp - a.timestamp);
  
  return (
    <div className={`${drugColor} bg-opacity-20 border-l-4 ${drugColor} rounded-lg p-3 sm:p-4 mb-2 sm:mb-3 shadow-md`}>
      <div className="flex justify-between items-start">
        <h3 className="text-lg sm:text-xl font-semibold text-white mb-2">{drug.name}</h3>
        <span className="text-xs text-gray-400 bg-gray-700 px-2 py-1 rounded-full">
          {entries.length} {entries.length === 1 ? 'dose' : 'doses'}
        </span>
      </div>
      
      <div className="text-sm sm:text-base text-gray-400">
        {sortedEntries.map(entry => (
          <div key={entry.id} className="flex justify-between items-start py-1">
            <div className="flex-1">
              <p>
                <span className="font-bold">{entry.dosage}{entry.unit}</span>@{formatTimestamp(entry.timestamp)} 
                <span className="text-gray-500"> ({formatTime(entry.timestamp)} ago)</span>
                {entry.notes && (
                  <span className="block mt-1 text-gray-300 italic text-xs">
                    {entry.notes}
                  </span>
                )}
              </p>
            </div>
            <button
              onClick={() => onDelete(entry.id)}
              className="text-gray-400 hover:text-red-500 transition-colors p-1 ml-2 mt-0"
              aria-label="Delete entry"
            >
              {React.createElement(Icons.FaTrash, { size: 14 })}
            </button>
          </div>
        ))}
      </div>
    </div>
  );
};

const ConsumptionLog: React.FC = () => {
  const { entries, removeEntry } = useConsumptionLog();
  const { drugs } = useDrugs();
  const [showEffectPlot, setShowEffectPlot] = useState<boolean>(false);
  
  // Force re-render whenever drugs change to ensure custom drugs are displayed
  const [, forceUpdate] = useState({});
  useEffect(() => {
    forceUpdate({});
  }, [drugs]);
  
  if (entries.length === 0) {
    return null;
  }

  // Convert ConsumptionEntry to Dose format required by EffectPlot
  const doseEntries: Dose[] = entries.map(entry => ({
    id: entry.id,
    drugId: entry.drugId,
    timestamp: entry.timestamp,
    dosage: entry.dosage,
    unit: entry.unit
  }));
  
  // Group entries by drugId
  const groupedEntries = entries.reduce((acc, entry) => {
    if (!acc[entry.drugId]) {
      acc[entry.drugId] = [];
    }
    acc[entry.drugId].push(entry);
    return acc;
  }, {} as Record<string, ConsumptionEntry[]>);
  
  return (
    <div className="mt-4 sm:mt-6">
      <ConsumptionOverview entries={entries} />
      
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg sm:text-xl font-semibold text-white">Consumption log</h3>
        <button
          onClick={() => setShowEffectPlot(!showEffectPlot)}
          className="px-3 py-1 bg-purple-700 hover:bg-purple-600 text-white rounded-lg text-sm sm:text-base flex items-center gap-1"
        >
          {showEffectPlot ? (
            <>
              {React.createElement(Icons.FaChartLine, { className: "text-xs" })}
              Hide effect plots
            </>
          ) : (
            <>
              {React.createElement(Icons.FaChartLine, { className: "text-xs" })}
              Show effect plots
            </>
          )}
        </button>
      </div>
      
      {showEffectPlot && (
        <div className="mb-6">
          <EffectPlot doses={doseEntries} />
        </div>
      )}
      
      <div className="space-y-2 sm:space-y-3">
        {entries.length === 0 ? (
          <p className="text-center text-gray-400 text-sm sm:text-base py-4">No entries yet. Add your first consumption above.</p>
        ) : (
          Object.entries(groupedEntries).map(([drugId, drugEntries]) => (
            <GroupedEntryCard
              key={drugId}
              drugId={drugId}
              entries={drugEntries}
              onDelete={removeEntry}
            />
          ))
        )}
      </div>
    </div>
  );
};

export default ConsumptionLog; 